import React, { useEffect, useRef, useState } from 'react';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'react-router-url-params';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from '../../../helpers/pagination-helper.ts';
import { useAdvancedForm } from '../../../helpers/form/useAdvancedForm.ts';
import { useWatch } from 'react-hook-form';
import {
  formatDate,
  formatDetailedDate,
} from '../../../helpers/abas-helper.ts';
import {
  DataGridRef,
  IColumnProps,
  Pager,
  Scrolling,
} from 'devextreme-react/data-grid';
import {
  DXG,
  useDataGrid,
} from '../../../components/devexpress/Grid/dx-grid.ts';
import {
  UserClient,
  UserQuery,
  UserRole,
  WebUsersClient,
} from 'services/api/api-client.ts';
import { Trans, UseTranslationResponse } from 'react-i18next';
import clsx from 'clsx';
import formStyles from '../../../components/form/form.module.scss';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import styles from './UserManagement.module.scss';
import { Input } from '../../../components/uikit/inputs/Input.tsx';
import {
  Button,
  ButtonColor,
} from '../../../components/uikit/buttons/Button.tsx';
import gridStyles from '../../../components/devexpress/Grid/grid.module.scss';
import {
  onContextMenuPreparing,
  TableHeaderCell,
} from '../../../helpers/tableHeaderCell/TableHeaderCell.tsx';
import { headerSortClick } from '../../../helpers/sort-helper.ts';
import { AppPagination } from '../../../components/uikit/pagination/AppPagination.tsx';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import Cross from 'assets/icons/close-button-small.svg?react';
import { NegativeActionText } from '../../../components/uikit/NegativeActionText.tsx';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { useModal } from '../../../components/uikit/modal/useModal.tsx';
import { Tooltip } from '../../../components/uikit/Tooltip.tsx';
import { Tip } from '../../../components/uikit/Tip.tsx';
import ActiveImage from '../../../assets/icons/success-round.svg';
import DeactivatedImage from '../../../assets/icons/status_failed.svg';
import PlusIcon from 'assets/icons/plus.svg?react';
import ImportIcon from 'assets/icons/import-users.svg?react';
import EditIcon from 'assets/icons/edit.svg?react';
import DotsIcon from 'assets/icons/dots-blue.svg?react';
import ActivateIcon from 'assets/icons/activate.svg?react';
import DeactivateIcon from 'assets/icons/deactivate.svg?react';
import { Links } from '../../../application/constants/links.ts';
import { ActionText } from '../../../components/uikit/ActionText.tsx';
import { UserSettingsModal } from './UserSettingsModal.tsx';
import { Popover } from '@mui/material';
import { CustomModal } from '../../../components/uikit/modal/CustomModal.tsx';
import { FeatureFlags } from '../../../application/constants/env-variables.ts';
import countries from 'i18n-iso-countries';

type Form = {
  searchByName: string;
};

export type SelectedUser = {
  name: string;
  email: string;
  isLocked: boolean;
  claimRoles: UserRole[];
  id: string;
  countryCode: string;
  customerNumber: string;
};

type Popover = {
  top: number;
  left: number;
};

const pageSizes = [10, 20, 50];

export const UserManagement = () => {
  const i18n = useScopedTranslation('Page.UserManagement');
  const [isImportTooltipVisible, setIsImportTooltipVisible] = useState(false);
  const [userActionsTooltipInfo, setUserActionsTooltipInfo] = useState('');
  const [isUserModalVisible, setIsUserModalVisible] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState<Popover | null>(null);
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);
  const [isActivateModalVisible, setIsActivateModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const ignoreNextScroll = useRef(false);

  const modals = useModal();
  const [params, setParams] = useQueryParams({
    searchByName: StringParam,
    ...pagingSortingQueryParams(pageSizes[0]),
    sortBy: withDefault(StringParam, 'createdAt'),
    desc: withDefault(BooleanParam, true),
  });

  const form = useAdvancedForm<Form>(
    async (data) => {
      setParams({ ...params, ...data, page: 1 });
    },
    {
      defaultValues: {
        searchByName: params.searchByName ?? '',
      },
    },
  );

  const usersData = UserQuery.useGetUserListQuery(
    {
      name: params.searchByName?.trim() ?? undefined,
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );
  const userQuery = UserQuery.useGetCurrentUserInfoQuery();
  const userRoles = userQuery.data?.claimRoles ?? [];
  const isAdmin = userRoles.includes(UserRole.Admin);

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));
  };

  const selectUser = (
    e: React.MouseEvent<HTMLDivElement>,
    data: SelectedUser,
  ) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();

    ignoreNextScroll.current = true;

    setPopoverPosition({
      top: rect.bottom,
      left: rect.right,
    });
    setSelectedUser(data);
  };

  const columnsConfig = getColumnsConfig(
    i18n,
    selectUser,
    selectedUser,
    popoverPosition,
    isAdmin
      ? []
      : [...userRoles, UserRole.SoftwareAccess, UserRole.FirmwareAccess],
  );
  const dataGridRef = useRef<DataGridRef<any, number>>(null);
  useDataGrid();

  const numberOfRows = usersData?.data?.data.length ?? 0;

  const paginationProps = {
    page: params.page,
    perPage: params.perPage,
    totalCount: usersData?.data?.totalCount ?? 0,
    pageSizes: pageSizes,
    numberOfRows: numberOfRows,
    changePagination: changePagination,
  };

  const changeSorting = (sortBy: string) => {
    setParams((prevParams) => ({
      ...prevParams,
      sortBy,
      desc: prevParams.sortBy !== sortBy ? false : !prevParams.desc,
      page: 1,
    }));
  };

  useEffect(() => {
    const handleScroll = () => {
      if (ignoreNextScroll.current) {
        ignoreNextScroll.current = false;
        return;
      }

      setPopoverPosition(null);
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const showUserActionsTooltip = (info: string) => {
    setUserActionsTooltipInfo(info);
    setTimeout(() => setUserActionsTooltipInfo(''), 3000);
  };

  const transferUsers = async () => {
    const login = await modals.showPrompt({
      text: (
        <div className={styles.marginBot5}>
          {i18n.t('transfer_user_modal_text')}
        </div>
      ),
      onSubmit(value) {
        if (!value) return i18n.t('required_error');
        return true;
      },
      title: i18n.t('transfer_user_modal_title'),
      defaultValue: '',
      fieldName: i18n.t('transfer_user_modal_fieldname'),
      okButtonText: i18n.t('transfer_user_modal_confirm'),
      cancelButtonText: i18n.t('transfer_user_modal_cancel'),
    });
    const trimmedLogin = login?.trim();

    if (trimmedLogin) {
      try {
        await WebUsersClient.transferUsersFromAbasByLogin(
          [trimmedLogin],
          undefined,
          true,
        );
        const result = await usersData.refetch();
        if (result) {
          setIsImportTooltipVisible(true);
          setTimeout(() => {
            setIsImportTooltipVisible(false);
          }, 3000);
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          await modals.showAlert({
            text: i18n.t('error_not_found', {
              login: trimmedLogin,
            }),
            title: i18n.t('error_modal_title'),
          });
        } else if (err.response?.status === 403) {
          await modals.showAlert({
            text: i18n.t('error_user_locked', {
              login: trimmedLogin,
            }),
            title: i18n.t('error_modal_title'),
          });
        } else {
          await modals.showAlert({
            text: err.detail,
            title: i18n.t('error_modal_title'),
          });
        }
      }
    }
  };

  const [searchByName] = useWatch({
    control: form.control,
    name: ['searchByName'],
  });

  const activateUser = async () => {
    try {
      if (selectedUser?.id) {
        setIsLoading(true);
        setIsActivateModalVisible(false);
        await UserClient.updateUserInformation(selectedUser?.id, {
          isLocked: !selectedUser?.isLocked,
        });
      }
    } finally {
      setIsLoading(false);
      setSelectedUser(null);
      showUserActionsTooltip(
        i18n.t(selectedUser?.isLocked ? 'user_activated' : 'user_deactivated'),
      );
      void usersData.refetch();
    }
  };

  const resetFilters = () => {
    form.reset({
      searchByName: '',
    });

    void form.handleSubmitDefault();
  };

  const isResetButtonShow = !!params.searchByName;

  const isPlaceholderVisible =
    !numberOfRows && !usersData.isLoading && !usersData.isPlaceholderData;

  const isActive = !selectedUser?.isLocked;

  return (
    <div data-test-id={'user-management-page'}>
      <Breadcrumbs customLinkForHomeIcon={Links.Authorized.Admin.route} />
      <Loading
        loading={
          usersData.isLoading || usersData.isPlaceholderData || isLoading
        }
      >
        <form onSubmit={form.handleSubmitDefault}>
          <div className={clsx(styles.rowContainer, styles.spaceBetween)}>
            <div className={clsx(styles.rowContainer, styles.columnGap15)}>
              <Input
                testId={'searchInput'}
                {...form.register('searchByName')}
                placeholder={i18n.t('search_by_name')}
                className={styles.width400}
                endAdornment={
                  <div>
                    {searchByName && (
                      <IconButton
                        type={'destroy'}
                        onClick={() => form.setValue('searchByName', '')}
                      >
                        <Cross />
                      </IconButton>
                    )}
                  </div>
                }
                endAdornmentClassname={styles.endAdornmentClassname}
              />
              <Button
                type={'submit'}
                className={clsx(formStyles.actionButton)}
                title={i18n.t('apply')}
                testId={'filters-apply-button'}
              />
              {isResetButtonShow && (
                <NegativeActionText
                  text={i18n.t('reset')}
                  onClickHandler={resetFilters}
                  containerClassName={styles.marginLeft10}
                  testId={'filters-reset-button'}
                />
              )}
            </div>
            <div className={clsx(styles.rowContainer, styles.columnGap24)}>
              <ActionText
                text={i18n.t('transfer_user')}
                Svg={ImportIcon}
                onClickHandler={transferUsers}
              />
              {FeatureFlags.isUserActionsEnabled() && (
                <ActionText
                  text={i18n.t('create_new_user')}
                  Svg={PlusIcon}
                  onClickHandler={() => {
                    setSelectedUser(null);
                    setIsUserModalVisible(true);
                  }}
                  testId={'create-user-modal-open-button'}
                />
              )}
            </div>
          </div>
        </form>
        <DXG.DataGrid
          ref={dataGridRef}
          dataSource={usersData.data?.data}
          className={clsx(
            gridStyles.editorOutline,
            formStyles.table,
            styles.fix,
          )}
          sorting={{ mode: 'none' }}
          paging={{ pageSize: numberOfRows }}
          noDataText={''}
          onContextMenuPreparing={onContextMenuPreparing}
          onCellClick={(event) => headerSortClick(event, changeSorting)}
        >
          <Scrolling columnRenderingMode={'virtual'} showScrollbar={'always'} />
          {columnsConfig.map((x, index) => {
            return (
              <DXG.Column
                cssClass={clsx(x.cssClass, formStyles.text)}
                key={index}
                showEditorAlways={x.allowEditing}
                headerCellRender={(cellInfo) => (
                  <TableHeaderCell
                    dataField={cellInfo.column.dataField}
                    caption={cellInfo.column.caption}
                    sortBy={params.sortBy}
                    desc={params.desc}
                  />
                )}
                {...x}
              />
            );
          })}
          <Pager visible={false} />
        </DXG.DataGrid>
        {isPlaceholderVisible && <TablePlaceholder />}
        {!!numberOfRows && <AppPagination {...paginationProps} />}
      </Loading>
      {isUserModalVisible && (
        <UserSettingsModal
          closeModal={() => setIsUserModalVisible(false)}
          refetch={usersData.refetch}
          showUserActionsTooltip={showUserActionsTooltip}
          selectedUser={selectedUser}
          userRoles={userRoles}
        />
      )}
      {isImportTooltipVisible && (
        <Tooltip
          closeTooltip={() => setIsImportTooltipVisible(false)}
          bodyContent={<div>{i18n.t('user_was_imported')}</div>}
          className={styles.tooltip}
          showCancelButton={true}
        />
      )}
      {userActionsTooltipInfo && (
        <Tooltip
          closeTooltip={() => setUserActionsTooltipInfo('')}
          bodyContent={<div>{userActionsTooltipInfo}</div>}
          className={styles.tooltip}
          showCancelButton={true}
        />
      )}
      <Popover
        className={styles.actionsShadow}
        open={!!popoverPosition}
        onClose={() => {
          setPopoverPosition(null);
        }}
        anchorReference={'anchorPosition'}
        anchorPosition={
          popoverPosition
            ? { top: popoverPosition.top, left: popoverPosition.left }
            : undefined
        }
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div>
          <ActionText
            className={styles.actionsTooltip}
            text={i18n.t('edit')}
            Svg={EditIcon}
            onClickHandler={() => {
              setPopoverPosition(null);
              setIsUserModalVisible(true);
            }}
            testId={'edit-user-modal-open-button'}
          />
          <ActionText
            testId={'activate-modal-button'}
            className={styles.actionsTooltip}
            text={i18n.t(selectedUser?.isLocked ? 'activate' : 'deactivate')}
            Svg={selectedUser?.isLocked ? ActivateIcon : DeactivateIcon}
            onClickHandler={() => {
              setPopoverPosition(null);
              setIsActivateModalVisible(true);
            }}
          />
        </div>
      </Popover>
      {isActivateModalVisible && (
        <CustomModal
          isOpen={true}
          onSubmit={activateUser}
          onCancel={() => setIsActivateModalVisible(false)}
          cancelButtonText={i18n.t('cancel')}
          submitButtonText={i18n.t(isActive ? 'deactivate' : 'activate')}
          submitButtonTestId={'activate-button'}
          titleText={`${i18n.t(isActive ? 'deactivate' : 'activate')} ${i18n.t(
            'user',
          )}`}
          containerClassName={styles.modalWidth480}
          textClassName={styles.lineHeight24}
          submitButtonType={
            isActive ? ButtonColor.Warning : ButtonColor.Primary
          }
        >
          <Trans
            i18nKey={`Page.UserManagement.${
              isActive ? 'deactivate_approve' : 'activate_approve'
            }`}
            values={{ username: selectedUser?.name }}
            components={{ bold: <strong /> }}
          />
        </CustomModal>
      )}
    </div>
  );
};

const getColumnsConfig = (
  i18n: UseTranslationResponse<'translation', 'Page.UserManagement'>,
  setSelectedUser: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    data: SelectedUser,
  ) => void,
  selectedUser: SelectedUser | null,
  popoverPosition: Popover | null,
  filterRoles: UserRole[],
): (IColumnProps & { disableExport?: boolean })[] => [
  {
    dataField: 'isLocked',
    allowEditing: false,
    allowSorting: true,
    caption: '',
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 52,
    cellRender: (row) => {
      const value = row.value;

      if (value) {
        return (
          <Tip
            className={styles.unsetBackgroundColor}
            tooltipText={i18n.t('deactivated_tooltip')}
            customImage={DeactivatedImage}
            testId={'deactivated-tooltip'}
          />
        );
      } else {
        return (
          <Tip
            className={styles.unsetBackgroundColor}
            tooltipText={i18n.t('active_tooltip')}
            customImage={ActiveImage}
            testId={'active-tooltip'}
          />
        );
      }
    },
  },
  {
    dataField: 'userName',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('customer_ID'),
    cssClass: clsx(
      formStyles.textCellPadding,
      formStyles.tableHeaderPointer,
      styles.noWrap,
    ),
    width: 120,
    cellRender: (cell) => {
      const isAbasUser = !!cell?.data?.customerNumber;
      const valueToRender = isAbasUser ? cell?.value ?? '' : '–';

      return (
        <div>
          <div data-test-id={isAbasUser ? 'customer-id' : undefined}>
            {valueToRender}
          </div>
          {!isAbasUser && (
            <div
              data-test-id={!isAbasUser ? 'customer-id' : undefined}
              className={styles.displayNone}
            >
              {cell?.value}
            </div>
          )}
        </div>
      );
    },
  },
  {
    dataField: 'name',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('name'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 285,
  },
  {
    dataField: 'email',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('email'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 285,
  },
  {
    dataField: 'countryCode',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('country'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 120,
    cellRender: (cell) => {
      const lang = i18n.i18n.language.split('-')[0];
      const countryName = countries.getName(cell.value, lang);
      return countryName || cell.value;
    },
  },
  {
    dataField: 'claimRoles',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('role'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 150,
    calculateCellValue: (cellValue) => {
      if (filterRoles.length) {
        return cellValue.claimRoles
          .filter((claimRole: UserRole) => filterRoles.includes(claimRole))
          .join(', ');
      }

      return cellValue.claimRoles.join(', ');
    },
  },
  {
    dataField: 'createdAt',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('date_of_transfer'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 160,
    cellRender: (cell) => formatDate(cell.value, true),
  },
  {
    dataField: 'lastSeenAt',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('last_seen'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 160,
    cellRender: (cell) => formatDetailedDate(cell.value, i18n.i18n),
  },
  {
    dataField: '',
    allowEditing: false,
    allowSorting: false,
    caption: '',
    visible: FeatureFlags.isUserActionsEnabled(),
    cssClass: formStyles.textCellPadding,
    width: 52,
    cellRender: (cell) => {
      const data = cell.data as SelectedUser;
      const isSelected = data.id === selectedUser?.id && !!popoverPosition;

      return (
        <div
          data-test-id={'user-actions-button'}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedUser(e, data);
          }}
          className={styles.dotsContainer}
        >
          <DotsIcon className={clsx(isSelected && styles.dotsActive)} />
        </div>
      );
    },
  },
];
